import { cn, tv } from "../foundations/cn.js";
import { RARITY_LABELS, RARITY_TEXT, type RarityTier } from "./rarity.js";

/**
 * A shape per tier on a 16-unit square, so the icon never says its tier by color alone: a rounded
 * square, a circle, an octagon, a diamond, a star, and the chase tier's spark.
 */
const RARITY_SHAPES: Record<RarityTier, string> = {
  common:
    "M4 2.5h8A1.5 1.5 0 0 1 13.5 4v8a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 12V4A1.5 1.5 0 0 1 4 2.5Z",
  uncommon: "M8 2.25a5.75 5.75 0 1 1 0 11.5a5.75 5.75 0 1 1 0-11.5Z",
  rare: "M5.5 2h5L14 5.5v5L10.5 14h-5L2 10.5v-5Z",
  epic: "M8 1l7 7-7 7-7-7Z",
  legendary:
    "M8 1.2L9.94 5.93L15.04 6.31L11.14 9.62L12.35 14.59L8 11.9L3.65 14.59L4.86 9.62L0.96 6.31L6.06 5.93Z",
  chase: "M8 0.5L9.7 6.3L15.5 8L9.7 9.7L8 15.5L6.3 9.7L0.5 8L6.3 6.3Z",
};

const ICON_SIZE = { sm: "size-3", md: "size-3.5", lg: "size-4" } as const;

export interface RarityIconProps {
  tier: RarityTier;
  size?: keyof typeof ICON_SIZE;
  className?: string;
}

/** The tier's shape in the tier's color, alone. Decorative: the word goes beside it or in sr text. */
export function RarityIcon({ tier, size = "md", className }: RarityIconProps) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
      className={cn("shrink-0 fill-current", ICON_SIZE[size], RARITY_TEXT[tier], className)}
    >
      <path d={RARITY_SHAPES[tier]} />
    </svg>
  );
}

const markerStyles = tv({
  base: "inline-flex max-w-full shrink-0 items-center whitespace-nowrap font-body text-text-primary",
  variants: {
    appearance: {
      plain: "gap-1.5",
      chip: "gap-1.5 rounded-pill border border-border-subtle bg-bg-raised",
    },
    size: { sm: "text-body-b3-strong", md: "text-body-b2-strong", lg: "text-body-b1-strong" },
  },
  compoundVariants: [
    { appearance: "chip", size: "sm", class: "h-6 px-2" },
    { appearance: "chip", size: "md", class: "h-7 px-2.5" },
    { appearance: "chip", size: "lg", class: "h-9 px-3.5" },
  ],
  defaultVariants: { appearance: "plain", size: "md" },
});

export interface RarityMarkerProps {
  tier: RarityTier;
  /** The word shown, in tenant copy; defaults to the tier's English name. */
  label?: string;
  /**
   * `false` shows the shape alone and keeps the word for screen readers, where the word is said
   * elsewhere or space is short (a dense grid). The shape still tells tiers apart without color.
   */
  showLabel?: boolean;
  /** `plain`: the shape and word inline. `chip`: on a raised pill, the reveal's tier callout. */
  appearance?: "plain" | "chip";
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * An item's rarity tier: its shape in the tier's color and its word in the text color, so it
 * reads without color and passes contrast on any theme. Rarity belongs to items, never boxes.
 */
export function RarityMarker({
  tier,
  label,
  showLabel = true,
  appearance,
  size = "md",
  className,
}: RarityMarkerProps) {
  const word = label ?? RARITY_LABELS[tier];
  return (
    <span data-tier={tier} className={markerStyles({ appearance, size, className })}>
      <RarityIcon tier={tier} size={size} />
      <span className={showLabel ? undefined : "sr-only"}>{word}</span>
    </span>
  );
}
